import type { PositionedGlyph } from './glyph-to-path';

export interface BridgePathResult {
  bridgePathData: string;
  dotChar: string;
  x: number;
  y: number;
}

/**
 * Automatically calculates bridges connecting floating 'i'/'j' dots and diacritics to the main glyph body.
 */
export function generateAutoBridges(
  glyphs: PositionedGlyph[],
  bridgeWidthMm = 1.5,
  _overhangMm = 0.5,
): BridgePathResult[] {
  const results: BridgePathResult[] = [];

  for (let i = 0; i < glyphs.length; i++) {
    const g = glyphs[i];
    const char = g.char.toLowerCase();

    // Candidate letters with floating dots
    const isDotCandidate =
      char === 'i' ||
      char === 'j' ||
      char === '!' ||
      char === '?' ||
      char === ';' ||
      char === ':' ||
      g.isFloating;

    if (!isDotCandidate) continue;

    // For standard 'i' or 'j' glyphs, create a central bridge in the upper 40% of the glyph
    const dotCenterX = (g.bounds.minX + g.bounds.maxX) / 2;
    const glyphHeight = g.bounds.height;

    // Dot sits approximately at the top 30%, stem at bottom 60%
    const bridgeTop = g.bounds.minY + glyphHeight * 0.15;
    const bridgeBottom = g.bounds.minY + glyphHeight * 0.55;
    const bridgeLeft = dotCenterX - bridgeWidthMm / 2;
    const bridgeRight = dotCenterX + bridgeWidthMm / 2;
    const radius = Math.min(bridgeWidthMm / 3, 0.4);

    const d = `M ${r(bridgeLeft + radius)} ${r(bridgeTop)} 
               L ${r(bridgeRight - radius)} ${r(bridgeTop)} 
               Q ${r(bridgeRight)} ${r(bridgeTop)} ${r(bridgeRight)} ${r(bridgeTop + radius)} 
               L ${r(bridgeRight)} ${r(bridgeBottom - radius)} 
               Q ${r(bridgeRight)} ${r(bridgeBottom)} ${r(bridgeRight - radius)} ${r(bridgeBottom)} 
               L ${r(bridgeLeft + radius)} ${r(bridgeBottom)} 
               Q ${r(bridgeLeft)} ${r(bridgeBottom)} ${r(bridgeLeft)} ${r(bridgeBottom - radius)} 
               L ${r(bridgeLeft)} ${r(bridgeTop + radius)} 
               Q ${r(bridgeLeft)} ${r(bridgeTop)} ${r(bridgeLeft + radius)} ${r(bridgeTop)} Z`;

    results.push({
      bridgePathData: d,
      dotChar: g.char,
      x: dotCenterX,
      y: (bridgeTop + bridgeBottom) / 2,
    });
  }

  return results;
}

function r(n: number): number {
  return Math.round(n * 100) / 100;
}
